import React, { useState, useMemo } from 'react';
import { Event } from '../types';
import { 
  Calendar, 
  User, 
  TrendingUp, 
  AlertTriangle, 
  FileText, 
  Users, 
  Award,
  ArrowRight,
  Filter,
  Search
} from 'lucide-react';

interface TimelineProps {
  events: Event[];
  selectedEvent: Event | null;
  onEventSelect: (event: Event) => void;
}

const eventTypeConfig = {
  'Document': { icon: FileText, color: 'bg-blue-500', textColor: 'text-blue-600' },
  'Hire': { icon: User, color: 'bg-blue-500', textColor: 'text-blue-600' },
  'Promotion': { icon: TrendingUp, color: 'bg-green-500', textColor: 'text-green-600' },
  'Role Change': { icon: ArrowRight, color: 'bg-indigo-500', textColor: 'text-indigo-600' },
  'Project Start': { icon: FileText, color: 'bg-purple-500', textColor: 'text-purple-600' },
  'Assignment': { icon: FileText, color: 'bg-cyan-500', textColor: 'text-cyan-600' },
  'Performance Review': { icon: Award, color: 'bg-yellow-500', textColor: 'text-yellow-600' },
  'Manager Change': { icon: Users, color: 'bg-teal-500', textColor: 'text-teal-600' },
  'Departure': { icon: ArrowRight, color: 'bg-orange-400', textColor: 'text-orange-600' },
  'Inflection Point': { icon: AlertTriangle, color: 'bg-orange-500', textColor: 'text-orange-600' },
  'Conflict': { icon: AlertTriangle, color: 'bg-red-500', textColor: 'text-red-600' },
  'Termination Event': { icon: AlertTriangle, color: 'bg-red-700', textColor: 'text-red-700' },
  'Termination': { icon: AlertTriangle, color: 'bg-black', textColor: 'text-black' },
};

const ratingMap = {
  "Needs Improvement": 1,
  "Meets Expectations": 2,
  "Exceeds Expectations": 3,
  "Strongly Exceeds Expectations": 4,
  "Superb": 5
};

const Timeline: React.FC<TimelineProps> = ({ events, selectedEvent, onEventSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique event types for filtering
  const eventTypes = useMemo(() => {
    const types = events.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(types).map(([type, count]) => ({
      type,
      count,
      label: type.replace(/([A-Z])/g, ' $1').trim()
    }));
  }, [events]);

  // Filter events based on search and type
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      const matchesSearch = searchTerm === '' || 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.manager?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.team?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = selectedType === 'all' || event.event_type === selectedType;
      
      return matchesSearch && matchesType;
    });
  }, [events, searchTerm, selectedType]);

  const createRatingSquares = (rating: string | null) => {
    if (!rating) return null;
    
    const level = ratingMap[rating as keyof typeof ratingMap] || 0;
    const squares = [];
    
    for (let i = 0; i < 5; i++) {
      const color = i < level ? 'bg-blue-500' : 'bg-gray-300';
      squares.push(
        <div key={i} className={`w-3 h-3 ${color} rounded-sm`}></div>
      );
    }
    
    return (
      <div className="flex items-center gap-1 mt-2">
        {squares}
        <span className="ml-2 text-xs font-medium text-gray-600">{rating}</span>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Search and Filter Controls */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="h-4 w-4 mr-1" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedType('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedType === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All ({events.length})
              </button>
              {eventTypes.map(({ type, count, label }) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {label} ({count})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No events found matching your criteria.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEvents.map((event, index) => {
              const config = eventTypeConfig[event.event_type as keyof typeof eventTypeConfig] || 
                           { icon: FileText, color: 'bg-gray-500', textColor: 'text-gray-600' };
              const Icon = config.icon;
              const isSelected = selectedEvent?.id === event.id;
              const isLast = index === filteredEvents.length - 1;

              return (
                <div
                  key={event.id}
                  className={`relative pl-12 pb-8 cursor-pointer transition-all duration-200 ${
                    isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => onEventSelect(event)}
                >
                  {/* Timeline Line */}
                  {!isLast && (
                    <div className="absolute left-5 top-5 h-full w-px bg-gray-300"></div>
                  )}
                  
                  {/* Timeline Point */}
                  <div className={`absolute left-5 top-5 w-5 h-5 rounded-full ${config.color} flex items-center justify-center ring-4 ring-white shadow-sm`}
                       style={{ transform: 'translateX(-50%)' }}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>

                  {/* Event Content */}
                  <div className={`${event.event_type === 'Inflection Point' ? 'p-4 bg-orange-50 border-2 border-dashed border-orange-400 rounded-lg' : 'pt-1'}`}>
                    <p className="text-sm font-medium text-gray-500">{formatDate(event.event_date)}</p>
                    <h4 className="font-bold text-lg mt-1 text-gray-900">{event.title}</h4>
                    
                    {/* Badges */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {event.level && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Level: {event.level}
                        </span>
                      )}
                      {event.role && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {event.role}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.textColor} bg-opacity-10`}>
                        {event.event_type}
                      </span>
                    </div>

                    {/* Details */}
                    {event.details && (
                      <p className="text-sm text-gray-600 mt-2">{event.details}</p>
                    )}

                    {/* Rating */}
                    {createRatingSquares(event.rating)}

                    {/* Note */}
                    {event.note && (
                      <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                        <p className="text-xs text-blue-800 italic">{event.note}</p>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="mt-2 space-y-1">
                      {event.manager && (
                        <p className="text-xs text-gray-500">Manager: {event.manager}</p>
                      )}
                      {event.team && (
                        <p className="text-xs text-gray-500">Team: {event.team}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Timeline;
